import { stringify } from 'querystring';

import { FeedType, User, UserFeedParameters } from '@ms/yammer-data/dist/state/types';

import { getDefaultFeedSortType } from '../feeds';
import { GetBaseUrlProps, getBaseUrl } from '../location';

export const userSortByKey = 'sortBy';
export const userAchievementTypeQueryKey = 'type';
export const userAchievementLevelQueryKey = 'level';
export const userAchievementPathQueryKey = 'path';
export const userAchievementCountQueryKey = 'count';

type UserPage = (userId: User['id']) => string;
export const userPageBasePath = '/users';
export const userPage: UserPage = (userId) => `${userPageBasePath}/${userId}`;
export const userStorylinePage: UserPage = (userId) => `${userPageBasePath}/${userId}/storyline`;
export const userAudienceFeedPage: UserPage = (userId) => `${userPageBasePath}/${userId}/audiencefeed`;
export const userMyBookmarksPage: UserPage = (userId) => `${userPageBasePath}/${userId}/bookmarks`;
export const userPendingPostsPage: UserPage = (userId) => `${userPageBasePath}/${userId}/pending-posts`;
export const userMyDraftsPage: UserPage = (userId) => `${userPageBasePath}/${userId}/pending-posts/drafts`;
export const userScheduledPostsPage: UserPage = (userId) => `${userPageBasePath}/${userId}/pending-posts/scheduled`;
export const userInsightsPage: UserPage = (userId) => `${userPageBasePath}/${userId}/overview`;
export const userInsightsInfluencePage: UserPage = (userId) => `${userPageBasePath}/${userId}/insights/influence`;

type UserPageUrl = (userId: User['id'], host: GetBaseUrlProps['host']) => string;
export const userPageUrl: UserPageUrl = (userId, host) => {
  const baseUrl = getBaseUrl({ host });

  return `${baseUrl}${userPage(userId)}`;
};

type UserStorylinePageUrl = (userId: User['id'], host: GetBaseUrlProps['host']) => string;
export const userStorylinePageUrl: UserStorylinePageUrl = (userId, host) => {
  const baseUrl = getBaseUrl({ host });

  return `${baseUrl}${userStorylinePage(userId)}`;
};

type UserMyBookmarksPageUrl = (userId: User['id'], host: GetBaseUrlProps['host']) => string;
export const userMyBookmarksPageUrl: UserMyBookmarksPageUrl = (userId, host) => {
  const baseUrl = getBaseUrl({ host });

  return `${baseUrl}${userMyBookmarksPage(userId)}`;
};

export enum UserPendingPostsType {
  DRAFTS = 'Drafts',
  SCHEDULED = 'Scheduled',
}

type UserPendingPostsPageUrl = (userId: User['id'], host: GetBaseUrlProps['host']) => string;
export const userPendingPostsPageUrl: UserPendingPostsPageUrl = (userId, host) => {
  const baseUrl = getBaseUrl({ host });

  return `${baseUrl}${userPendingPostsPage(userId)}`;
};
type UserMyDraftsPageUrl = (userId: User['id'], host: GetBaseUrlProps['host']) => string;
export const userMyDraftsPageUrl: UserMyDraftsPageUrl = (userId, host) => {
  const baseUrl = getBaseUrl({ host });

  return `${baseUrl}${userMyDraftsPage(userId)}`;
};

type UserScheduledPostsPageUrl = (userId: User['id'], host: GetBaseUrlProps['host']) => string;
export const userScheduledPostsPageUrl: UserScheduledPostsPageUrl = (userId, host) => {
  const baseUrl = getBaseUrl({ host });

  return `${baseUrl}${userScheduledPostsPage(userId)}`;
};

type UserInsightsPageUrl = (userId: User['id'], host: GetBaseUrlProps['host']) => string;
export const userInsightsPageUrl: UserInsightsPageUrl = (userId, host) => {
  const baseUrl = getBaseUrl({ host });

  return `${baseUrl}${userInsightsPage(userId)}`;
};

type UserInsightsInfluencePageUrl = (userId: User['id'], host: GetBaseUrlProps['host']) => string;
export const userInsightsInfluencePageUrl: UserInsightsInfluencePageUrl = (userId, host) => {
  const baseUrl = getBaseUrl({ host });

  return `${baseUrl}${userInsightsInfluencePage(userId)}`;
};

type UserFeedPageWithParameters = (userId: User['id'], userParameters: UserFeedParameters) => string;
export const userFeedPageWithParameters: UserFeedPageWithParameters = (userId, userParameters) =>
  userParameters.type === FeedType.UserWall
    ? `${userStorylinePage(userId)}${makeQueryStringWithUserFeedParameters(userParameters)}`
    : `${userPage(userId)}${makeQueryStringWithUserFeedParameters(userParameters)}`;

type MakeQueryStringWithUserFeedParameters = (userFeedParameters: UserFeedParameters) => string;
export const makeQueryStringWithUserFeedParameters: MakeQueryStringWithUserFeedParameters = ({ type, sortBy }) => {
  const sortByParameter = sortBy
    ? { [userSortByKey]: sortBy }
    : {
        [userSortByKey]: getDefaultFeedSortType(type),
      };

  return `?${stringify(sortByParameter)}`;
};
